/**
 * Custom logger plugin for Vue 3.
 *
 * This provides components with a logger object that can be used to log messages to the console. By default,
 * the logger is enabled and logs all messages with a log level of DEBUG or higher. The log level can be changed
 * by providing an options object when installing the plugin.
 */
import { injectStrict } from "../../helpers/provide-inject";
import { CustomLoggerProvider } from "./injection";
// Types & Enums
import { ref } from "vue";
import { LogLevels } from "./types";
// Constants
const logLevels = Object.values(LogLevels);
const defaultOptions = {
    isEnabled: true,
    logLevel: LogLevels.DEBUG,
    separator: "|",
    showLogLevel: false,
    showMethodName: false,
    stringifyArguments: false
};
const debugEmoji = "\u{1F41E}";
const voltEmoji = "\u26A1";
const infoEmoji = "\u2139\uFE0F";
const injectLogger = () => injectStrict(CustomLoggerProvider);
export { injectLogger };
// Plugin
export default {
    install: (app, options = {}) => {
        const opts = { ...defaultOptions, ...options };
        if (opts.showMethodName && !opts.showLogLevel)
            opts.showLogLevel = true;
        // Validate options
        if (!options.logLevel ||
            logLevels.indexOf(options.logLevel) === -1 ||
            (options.separator && options.separator.length > 2)) {
            throw new Error("Provided options for custom logger are not valid.");
        }
        const level = ref(options.logLevel);
        // From https://github.com/MarcSchaetz/vuejs3-logger/blob/8a62ecbaac4e7ad12d0a445634303b1a2ebb0607/src/vue3-logger.ts#L44
        const getMethodName = () => {
            let error;
            try {
                throw new Error("");
            }
            catch (e) {
                error = e;
            }
            // IE9 does not have .stack property
            if (error.stack === undefined)
                return "";
            let stackTrace = error.stack.split("\n")[3];
            if (/ /.test(stackTrace))
                stackTrace = stackTrace.trim().split(" ")[1];
            if (stackTrace && stackTrace.indexOf(".") > -1)
                stackTrace = stackTrace.split(".")[1];
            return stackTrace;
        };
        const printGroup = (emoji, logLevel, title, args) => {
            console.group(emoji, title.toUpperCase());
            console[logLevel](...args);
            console.groupEnd();
        };
        const print = (logLevel, args) => {
            switch (logLevel) {
                case LogLevels.DEBUG:
                    if (opts.showLogLevel) {
                        const title = options.showMethodName ? `${logLevel} ${opts.separator} ${getMethodName()}` : logLevel;
                        printGroup(debugEmoji, logLevel, title, args);
                    }
                    else
                        console[logLevel](...args);
                    break;
                case LogLevels.INFO:
                    if (opts.showLogLevel) {
                        const title = options.showMethodName ? `${logLevel} ${opts.separator} ${getMethodName()}` : logLevel;
                        printGroup(infoEmoji, logLevel, title, args);
                    }
                    else
                        console[logLevel](...args);
                    break;
                default:
                    console[logLevel](...args);
            }
        };
        const getLogger = (options) => {
            const logger = {};
            logLevels.reduce((acc, logLevel) => {
                if (logLevels.indexOf(logLevel) >= logLevels.indexOf(level.value) && options.isEnabled) {
                    logger[logLevel] = (...args) => {
                        let prefix = null;
                        if (options.showMethodName)
                            prefix = `${getMethodName()} ${options.separator} `;
                        const newArgs = prefix ? [prefix, ...args] : args;
                        const formattedArgs = options.stringifyArguments ? newArgs.map((a) => JSON.stringify(a)) : newArgs;
                        print(logLevel, formattedArgs);
                    };
                }
                else {
                    logger[logLevel] = () => undefined;
                }
                return acc;
            }, {});
            return logger;
        };
        app.provide(CustomLoggerProvider, getLogger(opts));
        // Expose a global setter for the log level
        window.setLogLevel = (newLogLevel) => {
            if (logLevels.indexOf(newLogLevel) === -1)
                throw new Error("Invalid log level");
            level.value = newLogLevel;
            console.log(`%c${voltEmoji} Log level set to ${newLogLevel} ${voltEmoji}`, "color: #ffcc00");
        };
    }
};
